import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PortfolioChartBox from './PortfolioChartBox';
import toast from 'react-hot-toast';
import { updateV2PortfolioVisibility, updatePortfolioThemeV1, updatePortfolioThemeV2, updateAdminPortfolio, getSnapshotDetail } from '../../composables/fetchAPI';
import ToggleCurrency from './../ToggleCurrency';
import { useExchangeRates } from '../../context/ExchangeRateContext';
import CostBasisModal from './CostBasisModal';
import { getPortfolioThemeStyle } from '../../utils/portfolioTheme'; // ปรับ Path ตามโครงสร้างโปรเจกต์ของคุณ
import { useMarketPrices } from '../../context/MarketPriceContext';
import { usePortfolio } from '../../context/PortfolioContext';
import ToggleSwitch from '../ToggleSwitch';
import ThemeColorPicker from './ThemeColorPicker'; // สมมติว่าไฟล์อยู่โฟลเดอร์เดียวกัน
import CreateShayPortfolioModal from './CreateShayPortfolioModal'
import CryptoPortfolioCard from './CryptoPortfolioCard';
import { isCryptoPortfolio, getPortfolioType } from '../../utils/portfolioType';
import { num } from '../../utils/cryptoDecimal';
const PortfolioAllocationBoxV2 = ({
  portfolios,
  shayPortfolios,
  onAddPortfolio,
  onEditPortfolio,
  onDeletePortfolio,
  isAdmin,
  setPortfolios,
  onToggleVisibility, // ✅ รับ Prop ตัวนี้เพิ่ม
  currency,
  handleCurrencyToggle
}: any) => {
  const navigate = useNavigate();
  const { exchangeRates } = useExchangeRates();
  const [isCostBasisModalOpen, setIsCostBasisModalOpen] = useState(false);
  const [editCostBasisData, setEditCostBasisData] = useState<any>(null);
  const { displayMode, refreshUserPortfolios, refreshAdminPortfolios, refreshShayPortfolios } = usePortfolio(); // ✅ ดึง displayMode จาก context
  const [isShayModalOpen, setIsShayModalOpen] = useState<any>();
  const [editShayData, setEditShayData] = useState<any>(null); // ✅ เพิ่ม State นี้
  // ✅ ทำการรวมพอร์ตทั้งหมดที่จะแสดง โดยถ้าเป็น Admin ให้เอา shayPortfolios มาต่อร่วมด้วย (หรือเลือกแสดงเฉพาะที่ต้องการ)
  const allDisplayPortfolios = useMemo(() => {
    const basePortfolios = Array.isArray(portfolios) ? portfolios : [];
    const adminShayList = Array.isArray(shayPortfolios) ? shayPortfolios : [];

    if (isAdmin && adminShayList.length > 0) {
      // รวมพอร์ตปกติและพอร์ต Shay เข้าด้วยกัน พร้อมกรองป้องกันไอดีซ้ำ
      const combined = [...basePortfolios, ...adminShayList];
      const uniqueMap = new Map();

      combined.forEach(p => {
        const id = p.portfolioId || p.portId || p.id || p.adminPortfolioId;
        if (id) uniqueMap.set(id, p);
      });

      return Array.from(uniqueMap.values());
    }

    return basePortfolios;
  }, [portfolios, shayPortfolios, isAdmin]);
  const [adminSnapshotsData, setAdminSnapshotsData] = useState<{ [key: string]: any }>({});

  // ✅ useEffect วิ่งไปดึง Snapshot Detail สำหรับพอร์ตแอดมินที่มี latestSnapshotId
  useEffect(() => {
    const fetchAdminSnapshots = async () => {
      const newSnapshots: { [key: string]: any } = {};

      for (const p of (allDisplayPortfolios || [])) {
        // เช็คว่าเป็นพอร์ตแอดมินที่มี latestSnapshotId และยังไม่เคยถูกดึงข้อมูลเก็บไว้
        const portId = p.adminPortfolioId || p.portfolioId;
        if (portId && p.latestSnapshotId && !adminSnapshotsData[portId]) {
          try {
            const res = await getSnapshotDetail(portId, p.latestSnapshotId);

            // ✅ แกะโครงสร้างจาก data.snapshot และ data.positions ที่ได้จาก API ของคุณ
            if (res?.data) {
              newSnapshots[portId] = {
                cashValue: res.data.snapshot?.cashValue || 0,
                positions: res.data.positions || []
              };
            }
          } catch (err) {
            console.error(`Failed to fetch snapshot for ${portId}:`, err);
          }
        }
      }

      if (Object.keys(newSnapshots).length > 0) {
        setAdminSnapshotsData(prev => ({ ...prev, ...newSnapshots }));
      }
    };

    fetchAdminSnapshots();
  }, [allDisplayPortfolios]);

  const [includeCash, setIncludeCash] = useState(() => {
    const saved = localStorage.getItem('includeCash');
    return saved ? JSON.parse(saved) : true; // default true
  });
  const [activeColorPickerPortId, setActiveColorPickerPortId] = useState<string | null>(null);
  // 2. ฟังก์ชัน toggle
  const handleToggleCash = () => {
    const newState = !includeCash;
    setIncludeCash(newState);
    localStorage.setItem('includeCash', JSON.stringify(newState));

    // ✅ ส่ง Event บอก Window ว่า localStorage มีการเปลี่ยนแปลง
    window.dispatchEvent(new Event('storage'));
  };
  // ✅ เพิ่มฟังก์ชันนี้เข้าไป
  const convertCurrency = (value: number, targetCurrency: string) => {
    if (targetCurrency === 'THB') {
      return value * (exchangeRates['THB'] || 1); // แปลงเป็นบาท
    }
    return value; // ถ้าเป็น USD คืนค่าเดิม
  };
  // ✅ 2. เรียกใช้งาน Context แทนการสร้าง State เอง
  const { currentPrices, fetchPricesForSymbols, isLoadingPrices } = useMarketPrices();

  // 1. คำนวณ Stats รวม (แยกหุ้น/เงินสด/กำไร)
  const stats = useMemo(() => {
    let totalStockMarketValue = 0;
    let totalCostValue = 0;
    let totalCash = 0;
    let totalRealizedGain = 0; // กำไรที่ขายแล้ว

    portfolios.forEach((portfolio: any) => {
      totalCash += num(portfolio.summary?.cashValue ?? portfolio.cashBalances?.[0]?.amount);
      // ดึง realizedGain จาก API ที่คุณส่งมา
      totalRealizedGain += num(portfolio.summary?.realizedGain);

      const activeSubPorts = (portfolio.subPorts || []).filter((sub: any) => sub.status === 'ACTIVE' || displayMode === 'V1');

      activeSubPorts.forEach((subPort: any) => {
        const items = subPort.holdings || subPort.stocks || [];
        items.forEach((item: any) => {
          const symbol = item.stockSymbol || item.assetSymbol || item.symbol;
          const price = currentPrices[symbol] || 0;
          // พอร์ตคริปโตส่งตัวเลขเป็น string ต้อง num() ก่อนบวก (แสดงผลเท่านั้น)
          const costVal = num(item.costValue) || (num(item.quantity) * num(item.avgEntryPrice || item.avgBuyPrice));

          totalStockMarketValue += num(item.quantity) * price;
          totalCostValue += costVal;
        });
      });
    });

    const unrealizedGain = totalStockMarketValue - totalCostValue;
    const exchangeRate = currency === 'THB' ? (exchangeRates['THB'] || 1) : 1;
    const totalValue = (totalStockMarketValue + totalCash) * exchangeRate;
    // ✅ 1. กำไร/ขาดทุนปกติ (Unrealized) - สำหรับแสดงผลแยกในกล่องเดิม
    const profit = unrealizedGain;

    // ✅ 2. ยอดใหม่: กำไรที่ขายแล้ว + กำไรที่ยังไม่ขาย
    const totalProfitCombined = totalRealizedGain + unrealizedGain;

    // ✅ 3. เปอร์เซ็นต์ใหม่: คำนวณจากกำไรเบ็ดเสร็จ
    const totalProfitPercent = totalCostValue > 0 ? (totalProfitCombined / totalCostValue) * 100 : 0;

    return {
      totalValue, // มูลค่ารวมรวมกำไรที่ขายแล้วตามโจทย์
      stockValue: totalStockMarketValue * exchangeRate,
      costValue: totalCostValue * exchangeRate,
      cashValue: totalCash * exchangeRate,

      // ส่งค่าไปโชว์
      profit: profit * exchangeRate, // กำไร Unrealized
      profitPercent: totalCostValue > 0 ? (profit / totalCostValue) * 100 : 0, // % Unrealized

      realizedGain: totalRealizedGain * exchangeRate, // กำไรที่ขายแล้ว
      realizedGainPercent: totalCostValue > 0 ? (totalRealizedGain / totalCostValue) * 100 : 0, // เปอร์เซ็นต์ realized

      // ค่าใหม่สำหรับตัวเลขใหญ่
      totalProfitCombined: totalProfitCombined * exchangeRate,
      totalProfitPercentCombined: totalProfitPercent
    };
  }, [portfolios, currentPrices, currency, exchangeRates, displayMode]);

  // // ดึงราคาหุ้น
  // useEffect(() => {
  //   if (!portfolios || portfolios.length === 0) return;
  //   const uniqueSymbols = new Set<string>();
  //   portfolios.forEach((p: any) => {
  //     p.subPorts?.forEach((sub: any) => {
  //       const stocks = sub.holdings || sub.stocks || [];
  //       stocks.forEach((s: any) => uniqueSymbols.add(s.assetSymbol || s.stockSymbol || s.symbol));
  //     });
  //   });
  //   const symbolArray = Array.from(uniqueSymbols).filter(Boolean);
  //   if (symbolArray.length > 0) fetchPricesForSymbols(symbolArray);
  // }, [portfolios]);
  // ฟังก์ชันสำหรับ toggle visibility
  const handleToggleVisibility = async (portId: any, currentStatus: string, portfolio: any) => {
    const newStatus = currentStatus === 'public' ? 'private' : 'public';
    try {

      if (portfolio.slug) {
        await updateAdminPortfolio(portId, { visibilityStatus: newStatus });
        toast.success(`Visibility updated to ${newStatus}`);
      } else {
        await updateV2PortfolioVisibility(portId, newStatus);
        toast.success(`Visibility updated to ${newStatus}`);
        setPortfolios((prevPortfolios: any[]) =>
          prevPortfolios.map((p) =>
            p.id === portId ? { ...p, visibilityStatus: newStatus } : p
          )
        );
      }

    } catch (error) {
      console.error('Error updating visibility:', error);
      toast.error('Failed to update visibility');
    }
  };

  // 1. ฟังก์ชันหา Market Value ของพอร์ตนั้นๆ
  // 1. ฟังก์ชันหา Market Value รวมของพอร์ตนั้นๆ (มูลค่าหุ้น + เงินสด)
  const getPortfolioActualValueUSD = (portfolio: any) => {
    let stockValue = 0;

    // 1. เช็คก่อนว่ามี subPorts ไหม ถ้าไม่มีหรือเป็น V1 ให้มองที่ portfolio.stocks หรือเอา subPorts ทั้งหมดโดยไม่กรอง status
    const subPorts = portfolio.subPorts && portfolio.subPorts.length > 0
      ? portfolio.subPorts.filter((sub: any) => displayMode === 'V1' || sub.status === 'ACTIVE' || !sub.status)
      : [];

    subPorts.forEach((subPort: any) => {
      const stocks = subPort.stocks || subPort.holdings || [];
      stocks.forEach((stock: any) => {
        const symbol = stock.stockSymbol || stock.assetSymbol || stock.symbol;
        const price = currentPrices[symbol] || 0;
        stockValue += num(stock.quantity) * price;
      });
    });

    // 2. เผื่อกรณีโหมด V1 ที่หุ้นเก็บไว้ที่ root ของ portfolio (เช่น portfolio.stocks)
    const rootStocks = portfolio.stocks || [];
    rootStocks.forEach((stock: any) => {
      const symbol = stock.stockSymbol || stock.assetSymbol || stock.symbol;
      const price = currentPrices[symbol] || 0;
      stockValue += num(stock.quantity) * price;
    });

    // 3. ดึงเงินสดมารวมตามเดิม
    const cashValue = portfolio.summary?.cashValue || portfolio.cashBalances?.[0]?.amount || portfolio.cashBalances?.amount || 0;

    return stockValue + Number(cashValue);
  };

  // 2. คำนวณ Market Value รวมเพื่อใช้เป็นฐานในการหาร
  // งบสัดส่วนหุ้น 100% กับคริปโต 100% แยกกัน จึงหารด้วยมูลค่ารวมของพอร์ตชนิดเดียวกัน
  const totalPortfoliosUSDByType = portfolios.reduce(
    (acc: Record<string, number>, p: any) => {
      const type = getPortfolioType(p);
      acc[type] = (acc[type] || 0) + getPortfolioActualValueUSD(p);
      return acc;
    },
    { STOCK: 0, CRYPTO: 0 } as Record<string, number>
  );

  // คำนวณมูลค่ารวมและกำไร/ขาดทุนรวม
  const calculateTotalValueAndProfit = () => {
    let totalCash = 0;
    let totalRealizedGain = 0;
    let totalCostUSD = 0;
    let totalStockMarketValue = 0;
    let totalCostValue = 0;
    portfolios.forEach((portfolio: any) => {
      // 1. รวมเงินสด (ดึงจาก summary หรือ cashBalances เหมือนที่ทำใน getPortfolioActualValueUSD)
      totalCash += num(portfolio.summary?.cashValue ?? portfolio.cashBalances?.[0]?.amount);
      // รวมกำไรที่ขายแล้ว
      totalRealizedGain += num(portfolio.summary?.realizedGain);

      const activeSubPorts = (portfolio.subPorts || []).filter((sub: any) => sub.status === 'ACTIVE' || displayMode === 'V1');
      activeSubPorts.forEach((subPort: any) => {
        const items = subPort.holdings || subPort.stocks || [];
        items.forEach((item: any) => {
          const symbol = item.stockSymbol || item.assetSymbol || item.symbol;
          const price = currentPrices[symbol] || 0;
          // พอร์ตคริปโตส่งตัวเลขเป็น string ต้อง num() ก่อนบวก (แสดงผลเท่านั้น)
          const costVal = num(item.costValue) || (num(item.quantity) * num(item.avgEntryPrice || item.avgBuyPrice));

          totalStockMarketValue += num(item.quantity) * price;
          totalCostValue += costVal;
        });
      });
    });

    const exchangeRate = currency === 'THB' ? (exchangeRates['THB'] || 1) : 1;
    const totalValue = (totalStockMarketValue + totalCash) * exchangeRate;
    const totalCost = totalCostUSD * exchangeRate;

    const overallProfit = totalValue - totalCost;
    const overallProfitPercent = totalCost > 0 ? (overallProfit / totalCost) * 100 : 0;

    return { totalValue, overallProfit, overallProfitPercent };
  };


  // ✅ 3. รวบรวมชื่อหุ้นและดึงราคาผ่าน Context แค่รอบเดียว
  useEffect(() => {
    if (!allDisplayPortfolios || allDisplayPortfolios.length === 0) return;

    const uniqueSymbols = new Set<string>();

    allDisplayPortfolios.forEach((p: any) => {
      // 1. กวาดจากพอร์ตปกติและพอร์ตย่อย
      if (p.stocks) {
        p.stocks.forEach((s: any) => uniqueSymbols.add(s.stockSymbol || s.symbol || s.assetSymbol));
      }
      if (p.subPorts) {
        p.subPorts.forEach((sub: any) => {
          if (sub.stocks) sub.stocks.forEach((s: any) => uniqueSymbols.add(s.stockSymbol || s.symbol || s.assetSymbol));
          if (sub.holdings) sub.holdings.forEach((h: any) => uniqueSymbols.add(h.assetSymbol || h.symbol || h.stockSymbol));
        });
      }

      // 2. ✅ เพิ่มตรงนี้: กวาดจากข้อมูล Snapshot ของพอร์ตแอดมิน / พอร์ต Shay (เช่น หุ้น S)
      const portId = p.adminPortfolioId || p.portfolioId || p.portId || p.id;
      const snap = adminSnapshotsData[portId];
      if (snap && snap.positions) {
        snap.positions.forEach((pos: any) => {
          if (pos.ticker) uniqueSymbols.add(pos.ticker);
        });
      }
    });

    const symbolArray = Array.from(uniqueSymbols).filter(Boolean);
    if (symbolArray.length > 0) {
      fetchPricesForSymbols(symbolArray);
    }
  }, [allDisplayPortfolios, adminSnapshotsData])

  const { totalValue, overallProfit, overallProfitPercent } = calculateTotalValueAndProfit();

  const handleAddPortfolio = () => {
    if (portfolios.length >= 6) {
      toast.error('พอร์ตจำกัดแค่ 6 พอร์ต');
      return;
    }
    onAddPortfolio();
  };
  // ตัวแปรเช็คสีของกล่องกำไร/ขาดทุน
  const isProfit = stats.totalProfitCombined >= 0;
  const plBorder = isProfit ? 'border-green-500/50' : 'border-red-500/50';
  const plBg = isProfit ? 'bg-green-500/10' : 'bg-red-500/10';
  const plText = isProfit ? 'text-green-500' : 'text-red-500';

  const handleUpdateTheme = async (portfolioId: string, themeValue: string) => {
    try {
      if (displayMode === 'V1') {
        await updatePortfolioThemeV1(portfolioId, themeValue);
      } else {
        await updatePortfolioThemeV2(portfolioId, themeValue);
      }
      refreshUserPortfolios()
      refreshShayPortfolios()
      toast.success('อัปเดตธีมพอร์ตเรียบร้อย');
    } catch (error) {
      toast.error('ไม่สามารถอัปเดตธีมได้');
    }
  };


  return (
    <div className="w-full px-4 md:px-16 py-2">
      <div
        className="flex flex-col gap-4 md:flex-row md:justify-between md:items-end mb-6 md:mb-8 border-b pb-6"
        style={{ borderColor: 'var(--text-input)' }}
      >
        {/* Stats Cards */}
        {/* ========================================================= */}
        {/* 📱 MOBILE VIEW: รวมทุกยอดไว้ในกล่องเดียวเป็น List */}
        {/* ========================================================= */}
        <div className="md:hidden flex flex-col w-full bg-[var(--component-bg-color)] rounded-xl border shadow-sm px-4 py-1" style={{ borderColor: 'var(--border-item-light)' }}>
          
          {/* 1. มูลค่าหุ้น / ต้นทุน */}
          <div className="flex justify-between items-center py-3 border-b" style={{ borderColor: 'var(--border-item-light)' }}>
            <div className="flex items-center gap-3">
              <div className="p-1.5 bg-yellow-500/10 rounded-lg shrink-0">
                <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path></svg>
              </div>
              <span className="text-[13px] font-bold text-gray-400" style={{ color: 'var(--text-color)' }}>มูลค่าหุ้น / ต้นทุน</span>
            </div>
            <div className="text-right">
              <div className="text-sm font-bold text-white mb-0.5" style={{ color: 'var(--text-color)' }}>
                {currency === "THB" ? "฿" : "$"}{stats.stockValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-[10px] font-medium text-gray-500" style={{ color: 'var(--text-color)' }}>
                ต้นทุน: {currency === "THB" ? "฿" : "$"}{stats.costValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>

          {/* 2. กำไร/ขาดทุน ทิพย์ */}
          {(() => {
            const isProfitLocal = stats.profit >= 0;
            const bgClass = isProfitLocal ? 'bg-green-500/10' : 'bg-red-500/10';
            const textClass = isProfitLocal ? 'text-green-500' : 'text-red-500';
            return (
              <div className="flex justify-between items-center py-3 border-b" style={{ borderColor: 'var(--border-item-light)' }}>
                <div className="flex items-center gap-3">
                  <div className={`p-1.5 ${bgClass} rounded-lg shrink-0`}>
                    <svg className={`w-4 h-4 ${textClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isProfitLocal ? "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" : "M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6"}></path></svg>
                  </div>
                  <span className="text-[13px] font-bold text-gray-400" style={{ color: 'var(--text-color)' }}>กำไร/ขาดทุน "ทิพย์"</span>
                </div>
                <div className="text-right">
                  <div className={`text-sm font-bold mb-0.5 ${textClass}`}>
                    {stats.profit > 0 ? '+' : ''}{currency === "THB" ? "฿" : "$"}{stats.profit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className={`text-[10px] font-bold ${textClass}`}>
                    ({stats.profitPercent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%)
                  </div>
                </div>
              </div>
            );
          })()}

          {/* 3. กำไรล็อคแล้ว */}
          <div className="flex justify-between items-center py-3 border-b" style={{ borderColor: 'var(--border-item-light)' }}>
            <div className="flex items-center gap-3">
              <div className="p-1.5 bg-green-500/10 rounded-lg shrink-0">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
              </div>
              <span className="text-[13px] font-bold text-gray-400" style={{ color: 'var(--text-color)' }}>กำไร "ล็อคแล้ว"</span>
            </div>
            <div className="text-right">
              <div className="text-sm font-bold text-green-500 mb-0.5">
                {currency === "THB" ? "฿" : "$"}{stats.realizedGain.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-[10px] font-bold text-green-500">
                ({stats.realizedGainPercent >= 0 ? '+' : '-'}{stats.realizedGainPercent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%)
              </div>
            </div>
          </div>

          {/* 4. เงินสด */}
          <div className="flex justify-between items-center py-3 border-b" style={{ borderColor: 'var(--border-item-light)' }}>
            <div className="flex items-center gap-3">
              <div className="p-1.5 bg-blue-500/10 rounded-lg shrink-0">
                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-bold text-gray-400" style={{ color: 'var(--text-color)' }}>เงินสด</span>
                <div className="scale-75 origin-left flex items-center"><ToggleSwitch isOn={includeCash} onToggle={handleToggleCash} /></div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm font-bold text-white" style={{ color: 'var(--text-color)' }}>
                {currency === "THB" ? "฿" : "$"}{stats.cashValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>

          {/* 5. มูลค่าพอร์ตรวม */}
          <div className="flex justify-between items-center py-3">
            <div className="flex items-center gap-3">
              <div className="p-1.5 bg-purple-500/10 rounded-lg shrink-0">
                <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"></path></svg>
              </div>
              <span className="text-[13px] font-bold text-gray-400" style={{ color: 'var(--text-color)' }}>มูลค่าพอร์ตรวม</span>
            </div>
            <div className="text-right">
              <div className="text-sm font-bold text-white mb-0.5" style={{ color: 'var(--yellow-text-color)' }}>
                {currency === "THB" ? "฿" : "$"}{stats.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className={`text-[10px] font-bold ${plText}`}>
                ({isProfit ? '+' : '-'}{stats.totalProfitPercentCombined.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%)
              </div>
            </div>
          </div>
        </div>

        {/* ========================================================= */}
        {/* 💻 DESKTOP VIEW: UI เดิม (Grid 5 กล่องแนวนอน) */}
        {/* ========================================================= */}
        <div className="hidden md:grid md:grid-cols-5 gap-4 w-full py-2 px-2">
          {/* กล่อง 1: มูลค่าหุ้น / ต้นทุน */}
          <div className="bg-[var(--component-bg-color)] p-4 rounded-xl border-2  border-yellow-500/50 shadow-sm flex flex-col justify-between min-w-[200px] md:min-w-0 transition-transform hover:scale-[1.02]">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 bg-yellow-500/10 rounded-lg">
                <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path></svg>
              </div>
              <div className="text-xs font-bold text-gray-400"
                style={{ color: 'var(--text-color)' }}
              >มูลค่าหุ้น / ต้นทุน</div>
            </div>
            <div>
              <div className="text-base font-bold text-white mb-1"
                style={{ color: 'var(--text-color)' }}
              >
                {currency === "THB" ? "฿" : "$"}{stats.stockValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-xs font-medium text-gray-500"
                style={{ color: 'var(--text-color)' }}
              >
                ต้นทุน: {currency === "THB" ? "฿" : "$"}{stats.costValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>

          {/* กล่อง 2: กำไร/ขาดทุนรวม (ทิพย์) */}
          {(() => {
            const isProfitLocal = stats.profit >= 0;
            const borderClass = isProfitLocal ? 'border-green-500/50' : 'border-red-500/50';
            const bgClass = isProfitLocal ? 'bg-green-500/10' : 'bg-red-500/10';
            const textClass = isProfitLocal ? 'text-green-500' : 'text-red-500';

            return (
              <div className={`bg-[var(--component-bg-color)] p-4 rounded-xl border-2 ${borderClass} shadow-sm flex flex-col justify-between min-w-[200px] md:min-w-0 transition-transform hover:scale-[1.02]`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className={`p-1.5 ${bgClass} rounded-lg`}>
                    {isProfitLocal ? (
                      <svg className={`w-4 h-4 ${textClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path></svg>
                    ) : (
                      <svg className={`w-4 h-4 ${textClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6"></path></svg>
                    )}
                  </div>
                  <div className="text-xs font-bold text-gray-400" style={{ color: 'var(--text-color)' }}>
                    กำไร/ขาดทุน "ทิพย์"
                  </div>
                </div>
                <div>
                  <div className={`text-base font-bold mb-1 ${textClass}`}>
                    {stats.profit > 0 ? '+' : ''}{currency === "THB" ? "฿" : "$"}{stats.profit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className={`text-[11px] font-bold ${textClass} flex items-center gap-1`}>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isProfitLocal ? "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" : "M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6"}></path>
                    </svg>
                    ({stats.profitPercent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%)
                  </div>
                </div>
              </div>
            );
          })()}

          {/* กล่อง 3: กำไรที่ขายแล้ว */}
          <div className="bg-[var(--component-bg-color)] p-4 rounded-xl border-2 border-green-500/50 shadow-sm flex flex-col justify-between min-w-[200px] md:min-w-0 transition-transform hover:scale-[1.02]">            
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 bg-green-500/10 rounded-lg">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
              </div>
              <div className="text-xs font-bold text-gray-400" style={{ color: 'var(--text-color)' }}>กำไร "ล็อคแล้ว"</div>
            </div>
            <div>
              <div className="text-base font-bold text-green-500 mb-1">
                {currency === "THB" ? "฿" : "$"}{stats.realizedGain.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-[11px] font-bold text-green-500">
                ({stats.realizedGainPercent >= 0 ? '+' : '-'}{stats.realizedGainPercent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%)
              </div>
            </div>
          </div>

          {/* กล่อง 4: เงินสด */}
          <div className="bg-[var(--component-bg-color)] p-4 rounded-xl border-2 border-blue-500/50 shadow-sm flex flex-col gap-2 min-w-[200px] md:min-w-0 transition-transform hover:scale-[1.02]">           
            <div className="flex items-center gap-2 ">
              <div className="p-1.5 bg-blue-500/10 rounded-lg">
                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
              </div>
              <div className="text-xs font-bold text-gray-400"
                style={{ color: 'var(--text-color)' }}
              >เงินสด</div> <ToggleSwitch isOn={includeCash} onToggle={handleToggleCash} />
            </div>
            <div>
              <div className="text-base font-bold text-white mb-1 "
                style={{ color: 'var(--text-color)' }}
              >
                {currency === "THB" ? "฿" : "$"}{stats.cashValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>

          {/* กล่อง 5: มูลค่าพอร์ต */}
          <div className="bg-[var(--component-bg-color)] p-4 rounded-xl border-2 border-purple-500/50 shadow-sm flex flex-col justify-between min-w-[200px] md:min-w-0 transition-transform hover:scale-[1.02]">            
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 bg-purple-500/10 rounded-lg">
                <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"></path></svg>
              </div>
              <div className="text-xs font-bold text-gray-400"
                style={{ color: 'var(--text-color)' }}
              >มูลค่าพอร์ตรวม</div>
            </div>
            <div>
              <div className="text-base font-bold text-white mb-1"
                style={{ color: 'var(--yellow-text-color)' }}
              >
                {currency === "THB" ? "฿" : "$"}{stats.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className={`text-[11px] font-bold ${plText} flex items-center gap-1`}>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isProfit ? "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" : "M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6"}></path>
                </svg>
                ({isProfit ? '+' : '-'}{stats.totalProfitPercentCombined.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%)
              </div>
            </div>
          </div>
        </div>
        {/* ======================================================================= */}
        {/* --- Action Buttons & Toggle Currency --- */}
        {isAdmin ? (
          // 👑 สำหรับ Admin: คง UI แบบเดิม (แยกชิ้นกัน ทำให้ตอน Mobile เรียงซ้อนลงมาในแนวตั้ง)
          <>
            <div className="flex flex-col items-center gap-2 md:gap-4 w-full md:w-auto">
              <button
                className="w-full md:w-auto bg-gradient-to-r from-purple-600 to-orange-500 hover:from-purple-700 hover:to-amber-600 text-black font-black px-4 py-3 rounded-lg shadow-lg transition-all active:scale-95 text-sm md:text-base"
                onClick={() => setIsShayModalOpen(true)}
              >
                👑 + พอร์ตจารย์เชย์
              </button>
              <button
                className="w-full md:w-auto bg-yellow-400 hover:bg-yellow-500 text-[#18181b] font-black px-4 py-3 rounded-lg shadow-lg transition-all active:scale-95 text-sm md:text-base"
                onClick={handleAddPortfolio}
              >
                + เพิ่มพอร์ต
              </button>
            </div>
            <div className="bg-[var(--component-bg-color)] p-1 rounded-lg shadow-sm border border-[var(--text-input)] mx-auto md:mx-0 w-max">
              <ToggleCurrency
                selectedCurrency={currency}
                onToggle={handleCurrencyToggle}
              />
            </div>
          </>
        ) : (
          // 👤 สำหรับ User ทั่วไป: ให้ปุ่ม "เพิ่มพอร์ต" กับ "Currency" อยู่บรรทัดเดียวกันในมือถือ (flex-row)
          <div className="flex flex-row items-stretch justify-between md:justify-end gap-3 w-full md:w-auto mt-2 md:mt-0">
            <button
              className="flex-1 md:flex-none bg-yellow-400 hover:bg-yellow-500 text-[#18181b] font-black px-4 py-2 md:py-3 rounded-lg shadow-lg transition-all active:scale-95 text-sm md:text-base flex items-center justify-center whitespace-nowrap"
              onClick={handleAddPortfolio}
            >
              + เพิ่มพอร์ต
            </button>
            <div className="bg-[var(--component-bg-color)] p-1 rounded-lg shadow-sm border border-[var(--text-input)] shrink-0 flex items-center justify-center">
              <ToggleCurrency
                selectedCurrency={currency}
                onToggle={handleCurrencyToggle}
              />
            </div>
          </div>
        )}
        {/* ======================================================================= */}
      </div>

      {portfolios?.length === 0 && (
        <div className="flex justify-center mt-20">
          <span className="text-gray-500">ยังไม่พบพอร์ตโปรด "เพิ่มพอร์ต"</span>
        </div>
      )}

      <div className="flex flex-wrap gap-6 justify-center w-full">
        {allDisplayPortfolios?.map((p: any) => {
          // const portValueUSD = getPortfolioActualValueUSD(p);
          // const actualRatio = totalAllPortfoliosUSD > 0
          //   ? (portValueUSD / totalAllPortfoliosUSD) * 100
          //   : 0;
          const currentPortId = p.portfolioId || p.portId || p.id
          const isShayPort = Boolean(p.slug);
          const chartData = displayMode === 'V1'
            ? (p.subPorts || []).map((sub: any) => ({
              ...sub,
              holdings: sub.stocks || [] // แปลง V1 stocks ให้เป็น V2 holdings
            }))
            : p.subPorts?.filter((sub: any) => sub.status === 'ACTIVE') || [];

          const portValueUSD = getPortfolioActualValueUSD(p);
          const sameTypeTotalUSD = totalPortfoliosUSDByType[getPortfolioType(p)] || 0;
          const actualRatio = sameTypeTotalUSD > 0
            ? (portValueUSD / sameTypeTotalUSD) * 100
            : 0;
          // 🎯 เพิ่มเงื่อนไขเช็คว่าเป็นพอร์ต Growth หรือไม่
          const portNameLower = (p.name || '').toLowerCase();
          const isGrowthPort = portNameLower.includes('พอร์ตรอง');
          // 🎨 เลือก class สำหรับกรอบเรืองแสงตามประเภทพอร์ต
          const glowContainerClass = isGrowthPort
            ? "glow-port-growth-container cursor-pointer transition-transform hover:scale-[1.02] border-0 w-full max-w-[420px]"
            : "glow-port-container cursor-pointer transition-transform hover:scale-[1.02] border-0 w-full max-w-[420px]";
          const isCryptoPort = !isShayPort && isCryptoPortfolio(p);
          return (
            <div key={currentPortId} className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] max-w-[420px] flex justify-center">
              {isCryptoPort ? (
                /* --- UI พอร์ตคริปโต (portfolioType=CRYPTO, USD เท่านั้น) --- */
                <CryptoPortfolioCard
                  portfolio={p}
                  actualRatio={actualRatio}
                  chartPortfolios={chartData}
                  cashAmount={num(p.summary?.cashValue ?? p.cashBalances?.[0]?.amount)}
                  currentPrices={currentPrices}
                  currency="USD"
                  loading={isLoadingPrices}
                  includeCash={includeCash}
                  isAdmin={isAdmin}
                  onOpen={() => navigate(`/portfolio/${currentPortId}`)}
                  onEdit={() => onEditPortfolio(p)}
                  onDelete={() => onDeletePortfolio(p)}
                  onToggleVisibility={() => handleToggleVisibility(currentPortId, p.visibilityStatus, p)}
                />
              ) : isShayPort ? (
                /* --- UI แบบใหม่: ใช้สไตล์เรืองแสงตลอดเวลาสำหรับพอร์ต 'shay' --- */
                <div
                  onClick={() => {
                    const portId = p.portfolioId || p.portId || p.id || p.adminPortfolioId;
                    if (p.slug) {
                      navigate(`/admin-Shay/${portId}`);
                    } else {
                      navigate(`/portfolio/${portId}`);
                    }
                  }}
                  className={glowContainerClass}
                  style={{ ...getPortfolioThemeStyle(p.settings?.themeConfig) }}
                >
                  {/* เนื้อหาการ์ดด้านใน */}
                  <div className="p-4 flex flex-col justify-center bg-[var(--component-bg-color)] rounded-[calc(0.9em-3px)] h-full">

                    <div className='flex justify-between items-center mb-2'>
                      <div className="text-xl font-bold flex items-baseline gap-2" style={{ color: 'var(--text-color)' }}>
                        <span>{p.name}</span>
                        {p.expectedRatio !== undefined && (
                          <span className="text-lg font-normal">
                            (<span style={{ color: 'var(--text-color)' }}>{actualRatio.toFixed(2)}%</span> / <span style={{ color: 'var(--yellow-text-color)' }}>{p.expectedRatio}%</span>)
                          </span>
                        )}
                      </div>
                      {isAdmin && (
                        <div
                          className="bg-yellow-400 text-black rounded shadow px-2 py-1 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleToggleVisibility(p.portfolioId || p.portId || p.id || p.adminPortfolioId, p.visibilityStatus, p);
                          }}
                        >
                          <span className=" text-[10px] font-bold text-black">นำเข้า</span>
                        </div>

                      )}


                      {p.settings?.costBasis?.preset &&
                        <button
                          // onClick={(e) => {
                          //   e.stopPropagation();
                          //   setEditCostBasisData(p);
                          //   setIsCostBasisModalOpen(true);
                          // }}
                          className="bg-gray-700 hover:bg-gray-600 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm transition-all active:scale-95 ml-2"
                        >
                          {/* แสดงชื่อ Preset ของพอร์ตนั้นๆ เช่น "WEBULL" หรือ "DIME" */}
                          <div className="text-gray-400 text-sm">
                            {p.settings?.costBasis?.preset === 'CUSTOM'
                              ? `${p.settings?.costBasis?.method}`
                              : p.settings?.costBasis?.preset
                            }
                          </div>
                        </button>
                      }
                      {/* --- เพิ่ม Icon พู่กันตรงนี้ --- */}
                      {/*  */}
                    </div>

                    <PortfolioChartBox
                      portfolios={(() => {
                        const currentId = p.adminPortfolioId || p.portfolioId || p.portId || p.id;
                        const snap = adminSnapshotsData[currentId];

                        // ถ้ายูสเซอร์มีข้อมูล snapshot แปะอยู่ ให้แปลงร่าง positions เป็น holdings ให้กราฟอ่านออก
                        if (snap && snap.positions) {
                          return [{
                            ...p,
                            summary: {
                              ...p.summary,
                              cashValue: snap.cashValue
                            },
                            holdings: snap.positions.map((pos: any) => ({
                              stockSymbol: pos.ticker,
                              assetSymbol: pos.ticker,
                              symbol: pos.ticker,
                              quantity: pos.quantity,
                              avgEntryPrice: pos.averageCost,
                            }))
                          }];
                        }

                        return chartData; // พอร์ตปกติทั่วไปใช้ข้อมูลเดิม
                      })()}
                      currentPrices={currentPrices}
                      useSubPorts={true}
                      fromAdmin={false}
                      currency={currency}
                      loading={isLoadingPrices}
                      cashAmount={
                        (adminSnapshotsData[p.adminPortfolioId || p.portfolioId || p.portId || p.id]?.cashValue)
                        ?? p.summary?.cashValue
                        ?? p.cashBalances?.[0]?.amount
                        ?? 0
                      }
                      includeCash={includeCash}
                      layout='horizontal'
                    />

                    <div className="flex items-center mt-4">
                      <button
                        className="w-20 h-10 flex items-center justify-center rounded text-white font-bold bg-red-500/80 hover:bg-red-500"
                        onClick={(e) => { e.stopPropagation(); onDeletePortfolio(p); }}
                      >
                        ลบ
                      </button>
                      <button
                        className="w-full h-10 ml-2 rounded text-[#18181b] font-bold bg-yellow-400/80 hover:bg-yellow-400"
                        onClick={(e) => { e.stopPropagation(); onEditPortfolio(p); }}
                      >
                        แก้ไข
                      </button>
                    </div>

                  </div>
                </div>
              ) : (
                /* --- UI แบบเดิม: สำหรับพอร์ตอื่นๆ ปกติ --- */
                <div
                  onClick={() => {
                    const portId = p.portfolioId || p.portId || p.id || p.adminPortfolioId;
                    if (p.slug) {
                      navigate(`/admin-Shay/${portId}`);
                    } else {
                      navigate(`/portfolio/${portId}`);
                    }
                  }} className="rounded-xl shadow-lg p-4 cursor-pointer flex flex-col justify-center transition-transform hover:scale-[1.02] border-2 w-full"
                  style={{ ...getPortfolioThemeStyle(p.settings?.themeConfig) }}
                >
                  <div className='flex justify-between'>
                    <div className="text-xl font-bold mb-2 flex items-baseline gap-2" style={{ color: 'var(--text-color)' }}>
                      <span>{p.name}</span>
                      {p.expectedRatio !== undefined && (
                        <span className="text-lg font-normal">
                          (<span style={{ color: 'var(--text-color)' }}>{actualRatio.toFixed(2)}%</span> / <span style={{ color: 'var(--yellow-text-color)' }}>{p.expectedRatio}%</span>)
                        </span>
                      )}
                    </div>
                    {isAdmin && (
                      <div
                        className="bg-yellow-400 text-black rounded shadow px-2 py-1 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleToggleVisibility(p.portfolioId || p.portId || p.id || p.adminPortfolioId, p.visibilityStatus, p);
                        }}
                      >
                        <span className=" text-[10px] font-bold text-black">นำเข้า</span>
                      </div>
                    )}


                    {p.settings?.costBasis?.preset &&
                      <button
                        // onClick={(e) => {
                        //   e.stopPropagation();
                        //   setEditCostBasisData(p);
                        //   setIsCostBasisModalOpen(true);
                        // }}
                        className="bg-gray-700 hover:bg-gray-600 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm transition-all active:scale-95 ml-2"
                      >
                        {/* แสดงชื่อ Preset ของพอร์ตนั้นๆ เช่น "WEBULL" หรือ "DIME" */}
                        <div className="text-gray-400 text-sm">
                          {p.settings?.costBasis?.preset === 'CUSTOM'
                            ? `${p.settings?.costBasis?.method}`
                            : p.settings?.costBasis?.preset
                          }
                        </div>
                      </button>
                    }
                    {/* --- เพิ่ม Icon พู่กันตรงนี้ --- */}
                    <div
                      className={`relative p-1.5 rounded-full cursor-pointer transition-colors ${activeColorPickerPortId === (p.portfolioId) ? 'bg-gray-700' : 'hover:bg-gray-700'
                        }`}
                      onClick={(e) => {
                        e.stopPropagation(); // ป้องกันคลิกโดน card
                        // เปิด picker ของพอร์ตนี้ (ถ้าเปิดอยู่แล้วให้ปิด)
                        setActiveColorPickerPortId(prev => prev === currentPortId ? null : currentPortId);
                      }}
                    >
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                        />
                      </svg>

                      {/* --- แสดงผล ThemeColorPicker Popup ตรงนี้ --- */}
                      {activeColorPickerPortId === currentPortId && (
                        <ThemeColorPicker
                          portfolioId={p.portfolioId || p.portId || p.id}
                          currentTheme={p.settings?.themeConfig || ''}
                          onUpdateTheme={handleUpdateTheme}
                          onClose={() => setActiveColorPickerPortId(null)} // สั่งปิดเมื่อกด OK
                        />
                      )}
                    </div>
                  </div>

                  <PortfolioChartBox
                    portfolios={(() => {
                      const currentId = p.adminPortfolioId || p.portfolioId || p.portId || p.id;
                      const snap = adminSnapshotsData[currentId];

                      // ถ้ายูสเซอร์มีข้อมูล snapshot แปะอยู่ ให้แปลงร่าง positions เป็น holdings ให้กราฟอ่านออก
                      if (snap && snap.positions) {
                        return [{
                          ...p,
                          summary: {
                            ...p.summary,
                            cashValue: snap.cashValue
                          },
                          holdings: snap.positions.map((pos: any) => ({
                            stockSymbol: pos.ticker,
                            assetSymbol: pos.ticker,
                            symbol: pos.ticker,
                            quantity: pos.quantity,
                            avgEntryPrice: pos.averageCost,
                          }))
                        }];
                      }

                      return chartData; // พอร์ตปกติทั่วไปใช้ข้อมูลเดิม
                    })()}
                    currentPrices={currentPrices}
                    useSubPorts={true}
                    fromAdmin={false}
                    currency={currency}
                    loading={isLoadingPrices}
                    cashAmount={
                      (adminSnapshotsData[p.adminPortfolioId || p.portfolioId || p.portId || p.id]?.cashValue)
                      ?? p.summary?.cashValue
                      ?? p.cashBalances?.[0]?.amount
                      ?? 0
                    }
                    includeCash={includeCash}
                    layout='horizontal'
                  />

                  <div className="flex items-center mt-4">
                    <button
                      className="w-20 h-10 flex items-center justify-center rounded text-white font-bold bg-red-500/80 hover:bg-red-500"
                      onClick={(e) => { e.stopPropagation(); onDeletePortfolio(p); }}
                    >
                      ลบ
                    </button>
                    <button
                      className="w-full h-10 ml-2 rounded text-[#18181b] font-bold bg-yellow-400/80 hover:bg-yellow-400"
                      onClick={(e) => {
                        e.stopPropagation();
                        // ✅ เช็คถ้ามี p.slug (พอร์ตแอดมิน) ให้เปิด Modal แทน
                        if (p.slug) {
                          setEditShayData(p);
                          setIsShayModalOpen(true);
                        } else {
                          // พอร์ตคนใช้ปกติ
                          onEditPortfolio(p);
                        }
                      }}
                    >
                      แก้ไข ชื่อพอร์ต / สัดส่วน
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {isCostBasisModalOpen && editCostBasisData && (
        <CostBasisModal
          isOpen={isCostBasisModalOpen}
          onClose={() => setIsCostBasisModalOpen(false)}
          portfolioId={editCostBasisData.portfolioId || editCostBasisData.portId || editCostBasisData.id}
          currentConfig={editCostBasisData.settings?.costBasis}
          onSave={() => {
            setIsCostBasisModalOpen(false);
            setPortfolios(); // รีเฟรชข้อมูลหน้า Dashboard
          }}
        />
      )}

      {/* Modal สร้างพอร์ตจารย์เชย์ */}
      <CreateShayPortfolioModal
        isOpen={isShayModalOpen}
        initialData={editShayData} // ✅ ส่งข้อมูลเดิมไปให้ Modal 
        onClose={() => {
          setIsShayModalOpen(false);
          setEditShayData(null); // ✅ เคลียร์ค่าเมื่อปิด
        }}
        onSuccessfullyCreated={() => {
          refreshShayPortfolios();
          refreshUserPortfolios();
        }}
      />


    </div>

  );
};

export default PortfolioAllocationBoxV2;